import React, { useState, useRef, useEffect } from "react";
import { Calendar, Copy, ImageOff, UserCheck, UserPlus, Users } from 'lucide-react';
import Button from '../shared/Button';
import ParticipantsModal from '../shared/ParticipantsModal';

const Dialog = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg p-6"
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

const EventCard = ({ event, isAuthenticated, onToggleSubscription, subscriptionLoading = false }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const descriptionRef = useRef(null);
  const [showReadMore, setShowReadMore] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (descriptionRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(descriptionRef.current).lineHeight);
      const height = descriptionRef.current.scrollHeight;
      setShowReadMore(height > lineHeight * 3);
    }
  }, [event.description]);

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      alert('Не удалось скопировать код. Пожалуйста, попробуйте еще раз.');
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg overflow-hidden h-full flex flex-col">
        {event.image_url ? (
          <div className="h-52 bg-gray-100">
            <img
              src={event.image_url}
              alt={event.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="h-52 bg-gradient-to-br from-slate-100 via-blue-50 to-cyan-50 border-b border-gray-100 flex flex-col items-center justify-center text-center px-6">
            <ImageOff className="text-slate-400 mb-2" size={28} />
            <p className="text-sm font-semibold text-slate-600">Картинка будет добавлена</p>
            <p className="text-xs text-slate-500 mt-1">А пока доступно описание события</p>
          </div>
        )}
        <div className="p-6 flex flex-col flex-grow">
          <div className="flex items-center mb-4">
            <Calendar className="text-custom-blue mr-2" size={20} />
            <span className="text-gray-600">
              {new Date(event.event_date).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>

          <h3 className="text-xl font-bold mb-2 text-gray-800">{event.title}</h3>

          <div className="flex-grow">
            {event.website_summary && (
              <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                {event.website_summary}
              </p>
            )}
            <div
              ref={descriptionRef}
              className="text-gray-600 line-clamp-3"
            >
              {event.description}
            </div>

            {Array.isArray(event.distances) && event.distances.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {event.distances.map((distance) => (
                  <span
                    key={distance}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                  >
                    {distance}
                  </span>
                ))}
              </div>
            )}

            {showReadMore && (
              <button
                onClick={() => setShowDialog(true)}
                className="text-custom-blue hover:text-custom-purple mt-2 font-medium text-sm"
              >
                Читать далее
              </button>
            )}
          </div>

          {event.price && (
            <p className="text-lg font-semibold text-custom-blue mt-4">
              {event.price} ₪
            </p>
          )}

          {event.coupon_code && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-sm text-gray-600 mb-2">Промокод:</p>
              <div className="flex justify-between items-center">
                <code className="text-custom-purple font-mono bg-gray-100 px-2 py-1 rounded">
                  {event.coupon_code}
                </code>
                <Button
                  onClick={() => copyToClipboard(event.coupon_code)}
                  className="!px-4 !py-2 text-sm relative"
                  disabled={copySuccess}
                >
                  <Copy size={16} className={`mr-2 ${copySuccess ? 'text-green-500' : ''}`} />
                  {copySuccess ? 'Скопировано!' : 'Копировать'}
                </Button>
              </div>
              {event.discount_amount && (
                <p className="text-sm text-gray-600 mt-2">
                  Скидка: {event.discount_amount}%
                </p>
              )}
            </div>
          )}

          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-gray-700 inline-flex items-center gap-1">
                <Users size={14} />
                Записались: <span className="font-semibold">{event.participants_count || 0}</span>
              </p>
              {isAuthenticated && (
                <button
                  type="button"
                  onClick={() => onToggleSubscription(event)}
                  disabled={subscriptionLoading}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    event.is_subscribed
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-[#4897d2] text-white hover:opacity-90'
                  } disabled:opacity-60`}
                >
                  {event.is_subscribed ? <UserCheck size={14} /> : <UserPlus size={14} />}
                  {subscriptionLoading ? 'Обработка...' : event.is_subscribed ? 'Вы записаны' : 'Записаться'}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowParticipantsModal(true)}
              className="text-xs text-[#4897d2] hover:underline mt-2 ml-1"
            >
              Участники
            </button>
          </div>

          <div className="mt-6">
            <a
              href={event.event_url || `#`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
            >
              <Button
                withArrow
                className="w-full"
                onClick={(e) => {
                  if (!event.event_url) {
                    e.preventDefault();
                    alert('Ссылка на событие пока недоступна');
                  }
                }}
              >
                Подробнее
              </Button>
            </a>
          </div>
        </div>
      </div>

      <Dialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
      >
        <h2 className="text-2xl font-bold mb-4">{event.title}</h2>
        {event.website_summary && (
          <p className="text-gray-500 mb-3">
            {event.website_summary}
          </p>
        )}
        <p className="text-gray-600 mb-6">
          {event.description}
        </p>
        {Array.isArray(event.distances) && event.distances.length > 0 && (
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-700 mb-2">Дистанции:</p>
            <div className="flex flex-wrap gap-2">
              {event.distances.map((distance) => (
                <span key={distance} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                  {distance}
                </span>
              ))}
            </div>
          </div>
        )}
        <Button
          onClick={() => setShowDialog(false)}
          className="w-full"
        >
          Закрыть
        </Button>
      </Dialog>

      <ParticipantsModal
        isOpen={showParticipantsModal}
        onClose={() => setShowParticipantsModal(false)}
        title={`Участники: ${event.title}`}
        participants={event.participants_preview || []}
        totalCount={event.participants_count || 0}
      />
    </>
  );
};

export default EventCard;
